import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Container } from 'rsuite';
import { changeSideMenu } from '../../../../../actions/actionsMenu';
import { LoadingSpinerArea } from '../../../../../components';
import { getAnaliseCompetitividadeRoute, mapMonitoramentoDetalheToAnaliseCompetitividade } from '../analiseCompetitividadeMapper';
import type { MonitoringItem } from '../Components';
import {
  FiltersBox,
  ModalConfirmarExclusao,
  ModalConfirmarRemoverParaMim,
  ModalPlanoMonitoramento,
  MonitoringTable,
  PageHeader,
  SearchBar,
  SummaryCards,
} from '../Components';
import { useMonitoramentos } from '../hooks/useMonitoramentos';
import { useMonitoramentosBigNumbers } from '../hooks/useMonitoramentosBigNumbers';
import { useUltimaExecucao } from '../hooks/useUltimaExecucao';
import { notify } from '../notify';
import { getMonitoramentoDetalhe } from '../services';
import styles from './CentralDeMonitoramento.module.scss';

export const CentralDeMonitoramento = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    items,
    isLoading,
    page,
    size,
    filters,
    searchValue,
    ativoFilter,
    emAlertaHojeFilter,
    totalElements,
    pendingToggleIds,
    onChangePage,
    onChangeSize,
    onChangeFilters,
    onChangeSearch,
    onChangeAtivoFilter,
    onToggleActive,
    onToggleEmAlertaHojeFilter,
    onDeleteMonitoramento,
    onRemoveForMe,
  } = useMonitoramentos();
  const { lastUpdate } = useUltimaExecucao();
  const { totalCriados, limitePlano, alertadosUltimos30Dias, emAlertaHoje } = useMonitoramentosBigNumbers();
  const [showPlanoModal, setShowPlanoModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<MonitoringItem | null>(null);
  const [itemToRemoveForMe, setItemToRemoveForMe] = useState<MonitoringItem | null>(null);

  useEffect(() => {
    dispatch(changeSideMenu('4')); // 4 = InfoPanelMenu
  }, [dispatch]);

  const handleHelpClick = () => {
    // TODO: Implementar modal/video de ajuda
    console.log('Veja como usar clicked');
  };

  const handleViewPlanClick = () => {
    setShowPlanoModal(true);
  };

  const handleClosePlanoModal = () => {
    setShowPlanoModal(false);
  };

  const handleRequestMorePlano = () => {
    // TODO: Implementar ação do botão "Quero mais monitoramentos"
    console.log('Quero mais monitoramentos clicked');
  };

  const handleOpenAnalysis = async (item: MonitoringItem) => {
    try {
      const { data: detalhe } = await getMonitoramentoDetalhe(item.id);
      const state = mapMonitoramentoDetalheToAnaliseCompetitividade(detalhe);

      history.push(getAnaliseCompetitividadeRoute(detalhe.tipo), state);
    } catch {
      notify('error', 5000, 'Não foi possível abrir a análise desse monitoramento.');
    }
  };

  const handleEdit = (item: MonitoringItem) => {
    history.push(`/isa/central-de-monitoramento/${item.id}/editar`);
  };

  const handleDuplicate = async (item: MonitoringItem) => {
    try {
      const { data: detalhe } = await getMonitoramentoDetalhe(item.id);
      history.push('/isa/central-de-monitoramento/criar', { duplicateFrom: detalhe });
    } catch {
      notify('error', 5000, 'Não foi possível carregar os dados desse monitoramento para criar um novo a partir dele.');
    }
  };

  const handleDelete = (item: MonitoringItem) => {
    setItemToDelete(item);
  };

  const handleCancelDelete = () => {
    setItemToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (itemToDelete) {
      onDeleteMonitoramento(itemToDelete.id);
    }
    setItemToDelete(null);
  };

  const handleToggleActive = (item: MonitoringItem, ativo: boolean) => {
    onToggleActive(item.id, ativo);
  };

  const handleRemoveForMe = (item: MonitoringItem) => {
    setItemToRemoveForMe(item);
  };

  const handleCancelRemoveForMe = () => {
    setItemToRemoveForMe(null);
  };

  const handleConfirmRemoveForMe = () => {
    if (itemToRemoveForMe) {
      onRemoveForMe(itemToRemoveForMe.id);
    }
    setItemToRemoveForMe(null);
  };

  const handleVisualizarEmAlertaHoje = () => {
    onToggleEmAlertaHojeFilter();
  };

  return (
    <Container className={`main-container ${styles.container}`}>
      <LoadingSpinerArea area="central-de-monitoramento" size="md" style={{ position: 'absolute' }} />
      <PageHeader lastUpdate={lastUpdate} onHelpClick={handleHelpClick} onViewPlanClick={handleViewPlanClick} />
      <SummaryCards
        totalCriados={totalCriados}
        totalDisponiveisNoPlano={limitePlano}
        alertadosUltimos30Dias={alertadosUltimos30Dias}
        emAlertaHoje={emAlertaHoje}
        emAlertaHojeFilterActive={!!emAlertaHojeFilter}
        onVisualizarEmAlertaHoje={handleVisualizarEmAlertaHoje}
      />
      <FiltersBox value={filters} onChange={onChangeFilters} />
      <SearchBar searchValue={searchValue} onSearchChange={onChangeSearch} ativoFilter={ativoFilter} onAtivoFilterChange={onChangeAtivoFilter} />
      <MonitoringTable
        data={items}
        isLoading={isLoading}
        page={page}
        size={size}
        totalElements={totalElements}
        pendingToggleIds={pendingToggleIds}
        onChangePage={onChangePage}
        onChangeSize={onChangeSize}
        onToggleActive={handleToggleActive}
        onOpenAnalysis={handleOpenAnalysis}
        onEdit={handleEdit}
        onDuplicate={handleDuplicate}
        onDelete={handleDelete}
        onRemoveForMe={handleRemoveForMe}
      />
      <ModalPlanoMonitoramento show={showPlanoModal} onClose={handleClosePlanoModal} onRequestMore={handleRequestMorePlano} />
      <ModalConfirmarExclusao
        show={!!itemToDelete}
        nomeMonitoramento={itemToDelete?.name}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
      <ModalConfirmarRemoverParaMim
        show={!!itemToRemoveForMe}
        nomeMonitoramento={itemToRemoveForMe?.name}
        onClose={handleCancelRemoveForMe}
        onConfirm={handleConfirmRemoveForMe}
      />
    </Container>
  );
};
